import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, InputAdornment, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, MenuItem, Paper, Skeleton, Stack, Tab, Tabs, TextField, Typography, } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { createCaseWithNewPatient, getAllCases } from '../supabase/crud';
import { getColorForLetter } from './avatarColor';
const HAIRLINE = 'rgba(15, 23, 42, 0.08)';
const HAIRLINE_SOFT = 'rgba(15, 23, 42, 0.06)';
const STAGES = ['To-do', 'Completed', 'Declined'];
const STAGE_SLUGS = {
    'To-do': 'to-do',
    Completed: 'completed',
    Declined: 'declined',
};
const STAGE_BY_SLUG = {
    'to-do': 'To-do',
    completed: 'Completed',
    declined: 'Declined',
};
const STAGE_COLOR = {
    'To-do': { bg: 'rgba(0, 115, 188, 0.1)', fg: '#0073BC' },
    Completed: { bg: 'rgba(54, 130, 63, 0.12)', fg: '#216432' },
    Declined: { bg: 'rgba(211, 47, 47, 0.1)', fg: '#C62828' },
};
const EMPTY_FORM = {
    firstName: '',
    lastName: '',
    rijksregister: '',
    mutualiteit: '',
    email: '',
    contact: '',
    rijksregisterMoeder: '',
    stage: 'To-do',
    status: '',
    status1: '',
    deadline: '',
    submittedOn: '',
    received: '',
    amount: '',
    paid: '',
    retroactive: '',
    monthly: '',
    tags: '',
};
const numOrUndefined = (s) => {
    const trimmed = s.trim();
    if (!trimmed)
        return undefined;
    const n = Number(trimmed);
    return Number.isNaN(n) ? undefined : n;
};
const strOrUndefined = (s) => (s.trim() ? s.trim() : undefined);
export default function CaseList() {
    const { stage: stageSlug } = useParams();
    const stageFilter = (stageSlug && STAGE_BY_SLUG[stageSlug]) || 'all';
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();
    const updateField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
    const fetchCases = async () => {
        try {
            const { cases } = await getAllCases();
            setCases(cases);
            setError(false);
        }
        catch (err) {
            console.error('Error fetching cases:', err);
            setError(true);
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchCases();
    }, []);
    const handleRetry = () => {
        setLoading(true);
        fetchCases();
    };
    const closeDialog = () => {
        if (saving)
            return;
        setDialogOpen(false);
        setForm(EMPTY_FORM);
    };
    const handleAddCase = async () => {
        if (!form.firstName.trim() || !form.lastName.trim())
            return;
        setSaving(true);
        try {
            const tags = form.tags
                .split(',')
                .map((t) => t.trim())
                .filter(Boolean);
            await createCaseWithNewPatient({
                email: strOrUndefined(form.email),
                contact: strOrUndefined(form.contact),
                rijksregisterMoeder: strOrUndefined(form.rijksregisterMoeder),
            }, {
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                rijksregister: strOrUndefined(form.rijksregister),
                mutualiteit: strOrUndefined(form.mutualiteit),
            }, {
                stage: form.stage,
                status: strOrUndefined(form.status),
                status1: strOrUndefined(form.status1),
                deadline: strOrUndefined(form.deadline),
                submittedOn: strOrUndefined(form.submittedOn),
                received: strOrUndefined(form.received),
                amount: numOrUndefined(form.amount),
                paid: numOrUndefined(form.paid),
                retroactive: numOrUndefined(form.retroactive),
                monthly: numOrUndefined(form.monthly),
                tags: tags.length ? tags : undefined,
            });
            setForm(EMPTY_FORM);
            setDialogOpen(false);
            await fetchCases();
        }
        catch (err) {
            console.error('Error adding case:', err);
        }
        finally {
            setSaving(false);
        }
    };
    const sortedCases = useMemo(() => [...cases].sort((a, b) => a.patient.firstName.localeCompare(b.patient.firstName)), [cases]);
    const stageFilteredCases = useMemo(() => {
        if (stageFilter === 'all')
            return sortedCases;
        return sortedCases.filter((c) => c.stage === stageFilter);
    }, [sortedCases, stageFilter]);
    const filteredCases = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query)
            return stageFilteredCases;
        return stageFilteredCases.filter((c) => `${c.patient.firstName} ${c.patient.lastName}`.toLowerCase().includes(query));
    }, [stageFilteredCases, search]);
    const stageCounts = useMemo(() => {
        const counts = {
            'To-do': 0,
            Completed: 0,
            Declined: 0,
        };
        sortedCases.forEach((c) => {
            if (c.stage)
                counts[c.stage] += 1;
        });
        return counts;
    }, [sortedCases]);
    const previewInitials = `${form.firstName[0] ?? ''}${form.lastName[0] ?? ''}`.toUpperCase();
    return (_jsxs(Box, { sx: {
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'background.default',
        }, children: [_jsxs(Box, { sx: {
                    flexShrink: 0,
                    width: '80%',
                    maxWidth: 1400,
                    mx: 'auto',
                    px: 4,
                    pt: 5,
                    pb: 3,
                }, children: [_jsxs(Stack, { direction: "row", sx: {
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            mb: 3.5,
                        }, children: [_jsxs(Stack, { direction: "row", spacing: 1.5, sx: { alignItems: 'center' }, children: [_jsx(Typography, { variant: "h4", sx: { fontWeight: 700, letterSpacing: '-0.02em' }, children: "Cases" }, void 0), !loading && !error && (_jsx(Chip, { label: cases.length, size: "small", sx: {
                                            bgcolor: HAIRLINE_SOFT,
                                            color: 'text.secondary',
                                            fontWeight: 700,
                                            height: 22,
                                            borderRadius: '6px',
                                            '& .MuiChip-label': { px: 1 },
                                        } }, void 0))] }, void 0), _jsx(Button, { variant: "contained", disableElevation: true, startIcon: _jsx(AddRoundedIcon, {}, void 0), onClick: () => setDialogOpen(true), children: "Add Case" }, void 0)] }, void 0), _jsx(TextField, { size: "small", placeholder: "Search cases by patient name", value: search, onChange: (e) => setSearch(e.target.value), sx: {
                            width: '100%',
                            maxWidth: 340,
                            bgcolor: 'background.paper',
                            '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                        }, slotProps: {
                            input: {
                                startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(SearchRoundedIcon, { fontSize: "small", color: "disabled" }, void 0) }, void 0)),
                            },
                        } }, void 0), _jsxs(Tabs, { value: stageFilter, onChange: (_e, value) => navigate(value === 'all' ? '/cases' : `/cases/${STAGE_SLUGS[value]}`), sx: {
                            mt: 2.5,
                            minHeight: 36,
                            borderBottom: '1px solid',
                            borderColor: HAIRLINE,
                            '& .MuiTab-root': {
                                minHeight: 36,
                                py: 1,
                                px: 1.5,
                                fontSize: 13.5,
                                fontWeight: 600,
                                textTransform: 'none',
                            },
                        }, children: [STAGES.map((stage) => (_jsx(Tab, { label: `${stage} (${stageCounts[stage]})`, value: stage }, stage))), _jsx(Tab, { label: `All (${sortedCases.length})`, value: "all" }, void 0)] }, void 0)] }, void 0), _jsx(Box, { sx: { flex: 1, overflow: 'auto', px: 4, pb: 5 }, children: _jsx(Box, { sx: { width: '80%', maxWidth: 1400, mx: 'auto' }, children: _jsx(Paper, { variant: "outlined", sx: {
                            borderRadius: '8px',
                            overflow: 'hidden',
                            borderColor: HAIRLINE,
                        }, children: error ? (_jsxs(Box, { sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                py: 8,
                                px: 3,
                            }, children: [_jsx(Box, { sx: {
                                        width: 56,
                                        height: 56,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(211, 47, 47, 0.08)',
                                        mb: 1,
                                    }, children: _jsx(ErrorOutlineRoundedIcon, { sx: { fontSize: 26, color: 'error.main' } }, void 0) }, void 0), _jsx(Typography, { sx: { fontWeight: 600 }, children: "Couldn't load cases" }, void 0), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 1 }, children: "Something went wrong while fetching your case list." }, void 0), _jsx(Button, { size: "small", variant: "outlined", startIcon: _jsx(RefreshRoundedIcon, {}, void 0), onClick: handleRetry, children: "Try again" }, void 0)] }, void 0)) : loading ? (_jsx(List, { disablePadding: true, children: Array.from({ length: 6 }).map((_, i) => (_jsxs(ListItem, { divider: i < 5, sx: { py: 1.5 }, children: [_jsx(ListItemAvatar, { children: _jsx(Skeleton, { variant: "circular", width: 38, height: 38 }, void 0) }, void 0), _jsx(ListItemText, { primary: _jsx(Skeleton, { width: "35%" }, void 0) }, void 0)] }, i))) }, void 0)) : filteredCases.length === 0 ? (_jsxs(Box, { sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                py: 8,
                                px: 3,
                            }, children: [_jsx(Box, { sx: {
                                        width: 56,
                                        height: 56,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: HAIRLINE_SOFT,
                                        mb: 1,
                                    }, children: _jsx(PeopleAltRoundedIcon, { sx: { fontSize: 26, color: 'text.disabled' } }, void 0) }, void 0), _jsx(Typography, { sx: { fontWeight: 600 }, children: cases.length === 0
                                        ? 'No cases yet'
                                        : search.trim()
                                            ? 'No matching cases'
                                            : `No ${stageFilter} cases` }, void 0), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: cases.length === 0 ? 1 : 0 }, children: cases.length === 0
                                        ? 'Add your first case to get started.'
                                        : search.trim()
                                            ? 'Try a different search.'
                                            : 'Try a different tab.' }, void 0), cases.length === 0 && (_jsx(Button, { size: "small", startIcon: _jsx(AddRoundedIcon, {}, void 0), onClick: () => setDialogOpen(true), children: "Add Case" }, void 0))] }, void 0)) : (_jsx(List, { disablePadding: true, children: filteredCases.map((c, i) => (_jsx(ListItem, { disablePadding: true, divider: i < filteredCases.length - 1, sx: { borderColor: HAIRLINE_SOFT }, children: _jsxs(ListItemButton, { disableRipple: true, disableTouchRipple: true, onClick: () => navigate(`/case/${c.id}`), sx: {
                                        py: 1.5,
                                        transition: 'background-color 0.12s ease',
                                        '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.035)' },
                                    }, children: [_jsx(ListItemAvatar, { children: _jsxs(Avatar, { sx: {
                                                    width: 38,
                                                    height: 38,
                                                    bgcolor: getColorForLetter(c.patient.firstName[0]),
                                                    fontWeight: 700,
                                                    fontSize: 13.5,
                                                }, children: [c.patient.firstName[0], c.patient.lastName[0]] }, void 0) }, void 0), _jsx(ListItemText, { primary: _jsxs(Typography, { sx: { fontWeight: 600, fontSize: 14.5 }, children: [c.patient.firstName, " ", c.patient.lastName] }, void 0), secondary: c.status || undefined }, void 0), c.stage && (_jsx(Chip, { label: c.stage, size: "small", sx: {
                                                bgcolor: STAGE_COLOR[c.stage].bg,
                                                color: STAGE_COLOR[c.stage].fg,
                                                fontWeight: 700,
                                                height: 22,
                                                borderRadius: '6px',
                                            } }, void 0))] }, void 0) }, c.id))) }, void 0)) }, void 0) }, void 0) }, void 0), _jsxs(Dialog, { open: dialogOpen, onClose: closeDialog, fullWidth: true, maxWidth: "sm", slotProps: { paper: { sx: { borderRadius: '10px' } } }, children: [_jsx(DialogTitle, { sx: { fontWeight: 700 }, children: "Add case" }, void 0), _jsx(Divider, {}, void 0), _jsxs(DialogContent, { children: [_jsxs(Stack, { direction: "row", spacing: 2, sx: { alignItems: 'center', pt: 2.5, pb: 3 }, children: [_jsx(Avatar, { sx: {
                                            width: 52,
                                            height: 52,
                                            fontWeight: 700,
                                            fontSize: 16,
                                            bgcolor: form.firstName
                                                ? getColorForLetter(form.firstName[0])
                                                : HAIRLINE_SOFT,
                                            color: form.firstName ? '#fff' : 'text.disabled',
                                            transition: 'background-color 0.15s ease',
                                        }, children: previewInitials || _jsx(PeopleAltRoundedIcon, { sx: { fontSize: 20 } }, void 0) }, void 0), _jsxs(Box, { children: [_jsx(Typography, { sx: { fontWeight: 600, fontSize: 14.5 }, children: form.firstName || form.lastName
                                                    ? `${form.firstName} ${form.lastName}`.trim()
                                                    : 'New patient' }, void 0), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "A client record is created automatically for this patient" }, void 0)] }, void 0)] }, void 0), _jsxs(Box, { component: "form", id: "add-case-form", onSubmit: (e) => {
                                    e.preventDefault();
                                    handleAddCase();
                                }, sx: { display: 'flex', flexDirection: 'column', gap: 3 }, children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx(Typography, { variant: "overline", sx: { color: 'text.secondary', letterSpacing: 1, lineHeight: 1 }, children: "Patient" }, void 0), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(TextField, { autoFocus: true, label: "First name", value: form.firstName, onChange: updateField('firstName'), fullWidth: true }, void 0), _jsx(TextField, { label: "Last name", value: form.lastName, onChange: updateField('lastName'), fullWidth: true }, void 0)] }, void 0), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(TextField, { label: "Rijksregister", value: form.rijksregister, onChange: updateField('rijksregister'), fullWidth: true }, void 0), _jsx(TextField, { label: "Mutualiteit", value: form.mutualiteit, onChange: updateField('mutualiteit'), fullWidth: true }, void 0)] }, void 0)] }, void 0), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx(Typography, { variant: "overline", sx: { color: 'text.secondary', letterSpacing: 1, lineHeight: 1 }, children: "Client (mother/contact)" }, void 0), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(TextField, { label: "Email", value: form.email, onChange: updateField('email'), fullWidth: true }, void 0), _jsx(TextField, { label: "Contact", value: form.contact, onChange: updateField('contact'), fullWidth: true }, void 0)] }, void 0), _jsx(TextField, { label: "Rijksregister (moeder)", value: form.rijksregisterMoeder, onChange: updateField('rijksregisterMoeder'), fullWidth: true }, void 0)] }, void 0), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx(Typography, { variant: "overline", sx: { color: 'text.secondary', letterSpacing: 1, lineHeight: 1 }, children: "Case" }, void 0), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(TextField, { select: true, label: "Stage", value: form.stage, onChange: updateField('stage'), fullWidth: true, children: STAGES.map((s) => (_jsx(MenuItem, { value: s, children: s }, s))) }, void 0), _jsx(TextField, { label: "Status", value: form.status, onChange: updateField('status'), fullWidth: true }, void 0)] }, void 0), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(TextField, { label: "Status (secondary)", value: form.status1, onChange: updateField('status1'), fullWidth: true }, void 0), _jsx(TextField, { label: "Deadline", type: "date", value: form.deadline, onChange: updateField('deadline'), slotProps: { inputLabel: { shrink: true } }, fullWidth: true }, void 0)] }, void 0), _jsx(TextField, { label: "Tags (comma separated)", value: form.tags, onChange: updateField('tags'), placeholder: "herziening, spraakcomputer", fullWidth: true }, void 0)] }, void 0), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx(Typography, { variant: "overline", sx: { color: 'text.secondary', letterSpacing: 1, lineHeight: 1 }, children: "Financial" }, void 0), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(TextField, { label: "Amount", type: "number", value: form.amount, onChange: updateField('amount'), fullWidth: true }, void 0), _jsx(TextField, { label: "Paid", type: "number", value: form.paid, onChange: updateField('paid'), fullWidth: true }, void 0)] }, void 0), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(TextField, { label: "Retroactive", type: "number", value: form.retroactive, onChange: updateField('retroactive'), fullWidth: true }, void 0), _jsx(TextField, { label: "Monthly", type: "number", value: form.monthly, onChange: updateField('monthly'), fullWidth: true }, void 0)] }, void 0)] }, void 0), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx(Typography, { variant: "overline", sx: { color: 'text.secondary', letterSpacing: 1, lineHeight: 1 }, children: "Tracking" }, void 0), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(TextField, { label: "Submitted on", type: "date", value: form.submittedOn, onChange: updateField('submittedOn'), slotProps: { inputLabel: { shrink: true } }, fullWidth: true }, void 0), _jsx(TextField, { label: "Received", type: "date", value: form.received, onChange: updateField('received'), slotProps: { inputLabel: { shrink: true } }, fullWidth: true }, void 0)] }, void 0)] }, void 0)] }, void 0)] }, void 0), _jsxs(DialogActions, { sx: { px: 3, pb: 2 }, children: [_jsx(Button, { onClick: closeDialog, disabled: saving, color: "inherit", children: "Cancel" }, void 0), _jsx(Button, { type: "submit", form: "add-case-form", variant: "contained", disableElevation: true, disabled: saving || !form.firstName.trim() || !form.lastName.trim(), startIcon: saving ? (_jsx(CircularProgress, { size: 16, color: "inherit" }, void 0)) : undefined, children: saving ? 'Saving…' : 'Save' }, void 0)] }, void 0)] }, void 0)] }, void 0));
}
